import React from 'react';

import { useQuery } from '@tanstack/react-query';
import { Field, Form } from 'react-final-form';

import { getEmailTemplates } from 'api/emailTemplates';
import { FormUpdateWatcher } from 'components/Pages/Admin/GuestPortal/GuestForms/FormUpdateWatcher';
import { BaseInput } from 'components/shared/BaseFormComponents/Input';
import { TogglerAdapter } from 'components/shared/BaseFormComponents/Toggler';
import Button from 'components/shared/Button';
import FormGroup from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Close from 'components/shared/Icon/icons/Close';
import FormTitle from 'components/shared/Typography/FormTitle';
import Text from 'components/shared/Typography/Text';
import { useEmailTemplateMutations } from 'hooks/useEmailTemplateMutations';
import useValidationSchema from 'hooks/useValidationSchema';

import * as decorators from './decorators';
import { DropdownSelect } from './DropdownSelect';
import s from './index.module.css';
import * as types from './types';
import { emailTemplateValidationSchema } from './validation';

function EmailTemplateForm({ templateFormState, setTemplateFormState, getLocale }: types.Props) {
  const validate = useValidationSchema(emailTemplateValidationSchema);
  const { createEmailTemplateMutation, updateEmailTemplateMutation } = useEmailTemplateMutations();

  const template = templateFormState?.values.template;

  const initialValues = React.useMemo(
    () => ({
      ...templateFormState?.values,
      subject: template?.subject ?? getLocale('subject'),
      body: template?.body ?? getLocale('body'),
      name: template?.name ?? '',
    }),
    [getLocale('body'), getLocale('subject'), template],
  );

  const emailTemplates = useQuery(['emailTemplates'], getEmailTemplates).data ?? [];
  const handledEmailTemplates = emailTemplates.map((template) => ({
    ...template,
    value: template.id,
    label: template.name,
  }));

  const handleSaveTemplate = async (formValues: types.FormValues) => {
    if (!formValues.isNewTemplate) return;

    try {
      const res = await createEmailTemplateMutation.mutateAsync(formValues);
      templateFormState?.form.change('template', res);
    } catch (error) {
      console.error('error', error);
    }
  };

  const [updating, setUpdating] = React.useState(false);
  const handleUpdateTemplate = async (formValues: types.FormValues) => {
    if (!formValues.template?.id) return;

    try {
      setUpdating(true);
      const res = await updateEmailTemplateMutation.mutateAsync({ ...formValues, id: formValues.template.id });
      templateFormState?.form.change('template', res);
    } catch (error) {
      console.error('error', error);
    } finally {
      setUpdating(false);
    }
  };

  const clearTemplate = () => {
    setTemplateFormState(undefined);
  };

  return (
    <Form
      onSubmit={handleSaveTemplate}
      validate={validate}
      initialValues={initialValues}
      decorators={[
        decorators.resetValidationOnToggleSaveAsTemplateDecorator,
        decorators.toggleSaveOnChangeTemplateDecorator,
      ]}
    >
      {({ values, handleSubmit, submitting }) => {
        return (
          <>
            <FormUpdateWatcher onUpdate={setTemplateFormState} />
            <div className={s.formGroup}>
              <FormTitle noBorder>Add a Custom Message</FormTitle>

              <div className={s.dropDownContainer}>
                {values.template && (
                  <div className={s.closeIcon}>
                    <Close onClick={clearTemplate} width="2rem" height="2rem" />
                  </div>
                )}

                <Field
                  name="template"
                  component={FormGroup}
                  customComponent={DropdownSelect}
                  placeholder="Search..."
                  noMargin
                  options={handledEmailTemplates}
                />
              </div>
            </div>

            <Field
              component={FormGroup}
              customComponent={BaseInput}
              isLabelInside
              name="subject"
              label="Subject Line*"
              parse={(value) => (value === '' ? null : value)}
              noMargin
            />

            <Field
              component={FormGroup}
              name="body"
              placeholder="Enter message here…"
              type="textarea"
              minRows={3}
              parse={(value) => (value === '' ? null : value)}
              componentClassName={s.textArea}
              noMargin
              className={s.fieldGap}
            />

            <div className={s.formGroup}>
              <Field
                component={FormGroup}
                customComponent={TogglerAdapter}
                type="checkbox"
                label="Save as New Template"
                theme="SECONDARY"
                isLabelInside
                noMargin
                name="isNewTemplate"
                componentClassName={s.togglerContainer}
                checkboxClassName={s.toggler}
              />

              <div className={s.updateContainer}>
                <Text type="brandon" className={s.textContainer}>
                  OR
                </Text>

                <div>
                  <Button
                    htmlType="button"
                    size="small"
                    className={s.saveTemplateButton}
                    onClick={() => handleUpdateTemplate(values)}
                    disabled={!values.name || submitting}
                    loading={updating}
                    type="primary2"
                  >
                    Update Template
                  </Button>
                </div>
              </div>
            </div>

            {values.isNewTemplate && (
              <div className={s.templateNameFieldContainer}>
                <Field
                  component={FormGroup}
                  customComponent={BaseInput}
                  isLabelInside
                  name="name"
                  label="Template Name*"
                  parse={(value) => (value === '' ? null : value)}
                  noMargin
                  className={s.nameInputContainer}
                />

                <div className={s.saveTemplateButtonContainer}>
                  <Button
                    size="small"
                    className={s.saveTemplateButton}
                    onClick={handleSubmit}
                    disabled={!values.name || updating}
                    loading={submitting}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </>
        );
      }}
    </Form>
  );
}

export { EmailTemplateForm };
